import type React from "react";
import {
  Button,
  Icon,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "@salestrak/ui";

import { useCustomFieldsDialogActions } from "#app/lib/custom-fields-dialog-context";
import { usePermission } from "#app/lib/hooks/use-permission";

const LABEL = "Open Custom Field Manager";

/**
 * The icon-only door to the Custom Field Manager that field-picking surfaces
 * carry in their chrome — the Customize view Fields page, the add-fields
 * dialog. One 24px ghost gear with the verb in its tooltip, so the manager
 * is reached the same way from every place that lists fields. Renders
 * nothing without the manage grant: the manager itself refuses the viewer,
 * so the door would open on a wall.
 */
export function FieldsManagerAction({
  onBeforeOpen,
  tooltipSide = "bottom",
}: {
  /** Runs before the manager opens — a host that is itself a modal closes
   *  here, so the manager does not stack on it. */
  onBeforeOpen?: () => void;
  tooltipSide?: React.ComponentProps<typeof TooltipPopup>["side"];
}) {
  const canManage = usePermission("custom_fields.manage");
  const { openDialog } = useCustomFieldsDialogActions();
  if (!canManage) return null;
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            size="icon-xs"
            variant="ghost"
            aria-label={LABEL}
            onClick={() => {
              onBeforeOpen?.();
              openDialog();
            }}
          />
        }
      >
        <Icon name="settings" color="muted" />
      </TooltipTrigger>
      <TooltipPopup side={tooltipSide}>{LABEL}</TooltipPopup>
    </Tooltip>
  );
}
